import type { ButtonHTMLAttributes } from "react"

import { cn } from "../../lib/utils"

type ButtonVariant = "primary" | "secondary" | "destructive" | "ghost"
type ButtonSize = "sm" | "md"

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
}

const baseClasses =
  "inline-flex items-center justify-center gap-1.5 font-medium transition-[color,background-color,border-color,opacity,transform] duration-200 ease-[var(--ease-product-out)] active:scale-[0.975] disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"

// Every button is a pill. Primary carries near-black/near-white ink (never
// brand); secondary is a white bordered pill that lifts off the stone canvas.
const variantClasses: Record<ButtonVariant, string> = {
  primary: "rounded-full bg-primary text-primary-foreground hover:bg-primary/88",
  secondary:
    "rounded-full border border-border bg-card text-foreground shadow-card hover:border-foreground/15 hover:bg-muted/50",
  destructive: "rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90",
  ghost: "rounded-full text-muted-foreground hover:bg-foreground/5 hover:text-foreground",
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-8 px-3.5 text-[13px]",
  md: "h-9 px-4 text-sm",
}

export function Button({
  variant = "secondary",
  size = "md",
  className,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(baseClasses, variantClasses[variant], sizeClasses[size], className)}
      {...rest}
    />
  )
}
